/**
 * ============================================================================
 * RiskBucketColumn — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: one of the four Kanban-style columns in the Risk Ranking
 *          Dashboard. Header carries the bucket label + count; body
 *          renders the supplier risk cards in priority order.
 * ============================================================================ */

import { useTranslation } from 'react-i18next';
import { EmptyState } from '@edms/design-system';
import { SupplierRiskCard } from './SupplierRiskCard';
import styles from './RiskRankingDashboard.module.css';
import type {
  RiskRankingBucket,
  RiskRankingCard,
} from './useRiskRankingQuery';

const CATEGORY_CLASS: Record<RiskRankingBucket['category'], string> = {
  CRITICAL: styles.colCritical,
  MAJOR: styles.colMajor,
  MINOR: styles.colMinor,
  LOW: styles.colLow,
};

export interface RiskBucketColumnProps {
  bucket: RiskRankingBucket;
  onOpenCard: (card: RiskRankingCard) => void;
}

export function RiskBucketColumn({ bucket, onOpenCard }: RiskBucketColumnProps) {
  const { t, i18n } = useTranslation();
  const nf = new Intl.NumberFormat(i18n.language);

  const label = t(`supplier.risk.${bucket.category}`, {
    defaultValue: bucket.category,
  });

  return (
    <section
      className={`${styles.column} ${CATEGORY_CLASS[bucket.category]}`}
      aria-label={t('supplier.riskRanking.columnAria', {
        defaultValue: '{{label}} risk bucket — {{n}} suppliers',
        label,
        n: nf.format(bucket.count),
      })}
    >
      <header className={styles.columnHeader}>
        <h2 className={styles.columnTitle}>{label}</h2>
        <span className={styles.columnCount} aria-hidden="true">
          {nf.format(bucket.count)}
        </span>
      </header>
      <div className={styles.columnBody}>
        {bucket.cards.length === 0 ? (
          <EmptyState
            compact
            icon="✓"
            title={t('supplier.riskRanking.bucketEmpty', { defaultValue: 'No suppliers here' })}
          />
        ) : (
          bucket.cards.map((c) => (
            <SupplierRiskCard
              key={c.supplier_id}
              card={c}
              onOpen={onOpenCard}
            />
          ))
        )}
      </div>
    </section>
  );
}
